/**
 * mergeWithFallback
 *
 * Merges two objects with nested properties, ensuring that all properties from the target are present in the result.
 * For properties that exist in the target but not in the source, the result will have those properties set to null.
 * Properties that exist in the source but not in the target will also be included in the result.
 *
 * @param target - The target object with nested properties.
 * @param source - The source object with nested properties.
 * @returns A new object that merges the source into the target, with missing properties set to null.
 */
export const mergeWithFallback = (
	target: { [key: string]: { [key: string]: boolean } },
	source: { [key: string]: { [key: string]: boolean } }
): { [key: string]: { [key: string]: boolean | null } } => {
	// Denis: If the target or source is not an object or has no keys, return an empty object.
	if (target === null || typeof target !== 'object' || source === null || typeof source !== 'object') {
		return {};
	}

	// Denis: Create a new object to store the result.
	const result: { [key: string]: { [key: string]: boolean | null } } = {};

	// Denis Iterate over the target keys
	Object.keys(target).forEach((key: string) => {
		// Denis: Check if the key is a direct property of the target
		if (target.hasOwnProperty(key)) {
			result[key] = {}; // Initialize the nested object for the current key

			// Denis: Go one level deeper to get the actual values
			Object.keys(target[key]).forEach((subKey: string) => {
				// Denis: Check if the value exists in the target to avoid en overwrite when not in the source.
				const targetValue = target[key] && target[key].hasOwnProperty(subKey) ? target[key][subKey] : null;
				// Denis: Check if the subKey is a direct property of the nested object
				// If the subKey exists in the source, use its value; otherwise, set it to null
				result[key][subKey] =
					source[key] && source[key].hasOwnProperty(subKey) ? source[key][subKey] : targetValue;
			});
		}
	});

	// Denis Iterate over the target keys
	Object.keys(source).forEach((key: string) => {
		// Denis: Check if the key is a direct property of the source
		if (source.hasOwnProperty(key)) {
			result[key] = {}; // Initialize the nested object for the current key

			// Denis: Go one level deeper to get the actual values
			Object.keys(source[key]).forEach((subKey: string) => {
				// Denis: Check if the subKey is a direct property of the nested object
				// If the subKey exists in the source, use its value; otherwise, set it to null
				result[key][subKey] = source[key] && source[key].hasOwnProperty(subKey) ? source[key][subKey] : null;
			});
		}
	});

	// Denis: Return the result
	return result;
};
